import React, { useState, useEffect } from "react";
import { useGetImagesQuery } from "state/api";
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Modal,
  Grid,
  Button,
  Chip,
  useMediaQuery,
  TextField,
  Autocomplete,
} from '@mui/material';
import { useDeletePresMutation } from "state/api";
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import RingLoader from 'react-spinners/RingLoader';
import FlexBetween from "components/FlexBetween";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import ArrowBackIosNew from '@mui/icons-material/ArrowBackIosNew';
import { toast } from "react-toastify";

// Robust date handling functions
const isValidDate = (dateStr) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  return !isNaN(date.getTime());
};

const formatDate = (dateStr) => {
  if (!isValidDate(dateStr)) return '';
  const date = new Date(dateStr);
  return date.toISOString().split('T')[0];
};

const formatDateForDisplay = (dateStr) => {
  if (!isValidDate(dateStr)) return '-';
  return new Date(dateStr).toLocaleDateString();
};

const ImagePreviewModal = () => {
  // Get current month/year for initial API call
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  
  // Pass month/year to API query
  const { data: appdata, error, isLoading, refetch } = useGetImagesQuery({ 
    month: currentMonth, 
    year: currentYear 
  });
  
  const [deletePres] = useDeletePresMutation();
  const [previewImage, setPreviewImage] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedEmpNo, setSelectedEmpNo] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedHeadquarter, setSelectedHeadquarter] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedProduct, setSelectedProduct] = useState("All"); // New state for product filter
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  useEffect(() => {
    if (appdata && !isLoading && !error) {
      setIsDataLoaded(true);
    }
  }, [appdata, isLoading, error]);

  const handleDelete = async (id) => {
    if (!id) return;
    
    if (window.confirm("Are you sure you want to delete this prescription?")) {
      try {
        setDeletingId(id);
        // Send deletion request with id in the body
        await deletePres({ id }).unwrap();
        toast.success("Prescription deleted successfully");
        refetch();
      } catch (error) {
        console.error("Delete failed:", error);
        toast.error(`Deletion failed: ${error.data?.message || error.message}`);
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handlePreviewImage = (pic) => {
    setPreviewImage(pic);
  };

  const handleDownload = (imageUrl) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = 'prescription.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Safely process data
  const safeAppData = Array.isArray(appdata) ? appdata : [];
  const sortedData = [...safeAppData]
    .map(item => ({
      ...item,
      formattedDate: formatDate(item.SUBDATE)
    }))
    .sort((a, b) => {
      const dateA = isValidDate(a.SUBDATE) ? new Date(a.SUBDATE) : new Date(0);
      const dateB = isValidDate(b.SUBDATE) ? new Date(b.SUBDATE) : new Date(0);
      return dateB - dateA;
    });

  const uniqueEmpNos = [...new Set(sortedData.map((item) => item.EmpNo))];
  const uniqueHeadquarters = [...new Set(sortedData.map(item => item.HeadQrt))].filter(Boolean);
  const uniqueStatuses = [...new Set(sortedData.map(item => item.STATUS))].filter(Boolean);
  const uniqueProducts = [...new Set(sortedData.map(item => item.product))].filter(Boolean); // Extract unique products

  // Filter data
  const filteredData = sortedData.filter((item) => {
    const matchesEmpNo = selectedEmpNo && selectedEmpNo !== "All" 
      ? item.EmpNo === selectedEmpNo 
      : true;
      
    const matchesDate = selectedDate 
      ? item.formattedDate === selectedDate
      : true;
      
    const matchesHeadquarter = selectedHeadquarter && selectedHeadquarter !== "All" 
      ? item.HeadQrt === selectedHeadquarter 
      : true;
      
    const matchesStatus = selectedStatus && selectedStatus !== "All" 
      ? item.STATUS === selectedStatus 
      : true;
      
    const matchesProduct = selectedProduct && selectedProduct !== "All" 
      ? item.product === selectedProduct 
      : true;
      
    return matchesEmpNo && matchesDate && matchesHeadquarter && matchesStatus && matchesProduct;
  });

  const CustomSpinner = () => (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      sx={{ backdropFilter: 'blur(5px)' }}
    >
      <RingLoader color={theme.palette.primary.main} size={100} />
    </Box>
  );

  if (isLoading) return <CustomSpinner />;
  
  if (error) {
    // Safely get error message
    const errorMessage = error.message || error.toString();
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: 3,
        textAlign: 'center',
        padding: 4,
        background: theme.palette.background.default
      }}>
        <Typography variant="h5" color="error">
          Failed to Load Data
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {errorMessage}
        </Typography>
        <Button 
          variant="contained" 
          color="primary"
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: theme.palette.background.default,
      padding: isMobile ? 2 : 4
    }}>
      {/* Header Section */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: `linear-gradient(45deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        boxShadow: 3,
        borderRadius: 2,
        mb: 4,
        p: 3
      }}>
        <FlexBetween>
          <Button
            onClick={() => navigate(-1)}
            startIcon={<ArrowBackIosNew />}
            sx={{
              color: theme.palette.common.white,
              '&:hover': {
                background: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            Back
          </Button>
          
          <Typography variant="h4" sx={{ 
            color: 'white',
            fontWeight: 700,
            textShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}>
            Prescriptions ({currentMonth}/{currentYear})
          </Typography>
          
          <Box sx={{ width: 100 }} /> {/* Spacer */}
        </FlexBetween>

        {/* Month/Year Selector */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          mb: 3,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <TextField
            label="Month"
            type="number"
            value={currentMonth}
            onChange={(e) => setCurrentMonth(Math.max(1, Math.min(12, parseInt(e.target.value) || 1)))}
            inputProps={{ min: 1, max: 12 }}
            variant="filled"
            sx={{ flexGrow: 1 }}
          />
          
          <TextField
            label="Year"
            type="number"
            value={currentYear}
            onChange={(e) => setCurrentYear(Math.max(2000, parseInt(e.target.value) || new Date().getFullYear()))}
            inputProps={{ min: 2000, max: 2100 }}
            variant="filled"
            sx={{ flexGrow: 1 }}
          />
          
          <Button
            variant="contained"
            color="primary"
            onClick={() => refetch()}
            sx={{ px: 4 }}
          >
            Load Data
          </Button>
        </Box>

        {/* Filter Section */}
        <Box sx={{ 
          mt: 2,
          display: 'flex',
          gap: 2,
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: 'wrap'
        }}>
          {/* Employee Filter */}
          <Autocomplete
            options={["All", ...uniqueEmpNos]}
            value={selectedEmpNo}
            onChange={(event, newValue) => setSelectedEmpNo(newValue || "All")}
            sx={{ flexGrow: 1, minWidth: isMobile ? '100%' : 150 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by Employee"
                variant="filled"
                sx={{
                  background: theme.palette.background.paper,
                  borderRadius: 2,
                  '& .MuiFilledInput-root': {
                    background: 'transparent'
                  }
                }}
              />
            )}
          />
          
          {/* Date Filter */}
          <TextField
            type="date"
            label="Filter by Date"
            value={selectedDate || ''}
            onChange={(e) => setSelectedDate(e.target.value || null)}
            variant="filled"
            sx={{ flexGrow: 1 }}
            InputLabelProps={{
              shrink: true,
            }}
          />
          
          {/* Headquarters Filter */}
          <Autocomplete
            options={["All", ...uniqueHeadquarters]}
            value={selectedHeadquarter}
            onChange={(event, newValue) => setSelectedHeadquarter(newValue || "All")}
            sx={{ flexGrow: 1, minWidth: isMobile ? '100%' : 150 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by Headquarters"
                variant="filled"
                sx={{
                  background: theme.palette.background.paper,
                  borderRadius: 2,
                  '& .MuiFilledInput-root': {
                    background: 'transparent'
                  }
                }}
              />
            )}
          />
          
          {/* Status Filter */}
          <Autocomplete
            options={["All", ...uniqueStatuses]}
            value={selectedStatus}
            onChange={(event, newValue) => setSelectedStatus(newValue || "All")}
            sx={{ flexGrow: 1, minWidth: isMobile ? '100%' : 150 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by Status"
                variant="filled"
                sx={{
                  background: theme.palette.background.paper,
                  borderRadius: 2,
                  '& .MuiFilledInput-root': {
                    background: 'transparent'
                  }
                }}
              />
            )}
          />
          
          {/* Product Filter */}
          <Autocomplete
            options={["All", ...uniqueProducts]}
            value={selectedProduct}
            onChange={(event, newValue) => setSelectedProduct(newValue || "All")}
            sx={{ flexGrow: 1, minWidth: isMobile ? '100%' : 150 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by Product"
                variant="filled"
                sx={{
                  background: theme.palette.background.paper,
                  borderRadius: 2,
                  '& .MuiFilledInput-root': {
                    background: 'transparent'
                  }
                }}
              />
            )}
          />

          <Button
            onClick={() => {
              setSelectedEmpNo("All");
              setSelectedDate(null);
              setSelectedHeadquarter("All");
              setSelectedStatus("All");
              setSelectedProduct("All");
            }}
            variant="contained"
            color="secondary"
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 4,
              minWidth: isMobile ? '100%' : 'auto'
            }}
          >
            Clear Filters
          </Button>
        </Box>
      </Box>

      {/* Content Grid */}
      <Grid container spacing={3} sx={{ px: isMobile ? 0 : 2 }}>
        {filteredData.length > 0 ? (
          filteredData.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Card sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 3,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}>
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    image={item.pic}
                    alt={`Prescription for ${item.DoctName || 'unknown'}`}
                    onClick={() => handlePreviewImage(item.pic)}
                    sx={{
                      height: 250,
                      cursor: 'zoom-in',
                      objectFit: 'cover',
                      borderTopLeftRadius: 3,
                      borderTopRightRadius: 3
                    }}
                  />
                  <Chip
                    label={item.STATUS || 'Unknown'}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 10,
                      left: 10,
                      background: item.STATUS === 'Approved' 
                        ? theme.palette.success.light 
                        : item.STATUS === 'Pending'
                          ? theme.palette.warning.light 
                          : theme.palette.error.light,
                      fontWeight: 700,
                      color: 'white'
                    }}
                  />
                  
                  {/* Delete Button */}
                  <IconButton
                    onClick={() => handleDelete(item.id)}
                    disabled={deletingId === item.id}
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      backgroundColor: theme.palette.error.main,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: theme.palette.error.dark,
                      },
                    }}
                  >
                    {deletingId === item.id ? (
                      <RingLoader 
                        color={theme.palette.common.white} 
                        size={20} 
                      />
                    ) : (
                      <DeleteIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>

                <CardContent sx={{ bgcolor: theme.palette.background.paper }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 700,
                    mb: 1,
                    color: theme.palette.text.primary
                  }}>
                    {item.NAME || 'Unknown'}
                  </Typography>
                  
                  <Grid container spacing={1}>
                    {[
                      { label: 'id', value: item.id },
                      { label: 'Emp No', value: item.EmpNo },
                      { label: 'Doctor', value: item.DoctName },
                      { label: 'Category', value: item.DocCategory },
                      { label: 'Product', value: item.product },
                      { label: 'MCL Code', value: item.MclCode },
                      { label: 'Submitted', value: formatDateForDisplay(item.SUBDATE) },
                      { label: 'Headquarters', value: item.HeadQrt },
                      { label: 'Status', value: item.STATUS }
                    ].map((field, i) => (
                      <Grid item xs={6} key={i}>
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5
                        }}>
                          <Typography variant="caption" sx={{
                            color: theme.palette.text.secondary,
                            fontWeight: 500
                          }}>
                            {field.label}
                          </Typography>
                          <Typography variant="body2" sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            wordBreak: 'break-word'
                          }}>
                            {field.value || '-'}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Box sx={{
            width: '100%',
            textAlign: 'center',
            py: 10
          }}>
            <Typography variant="h6" color="textSecondary">
              {isDataLoaded ? "No prescriptions found" : "Loading prescriptions..."}
            </Typography>
          </Box>
        )}
      </Grid>

      {/* Preview Modal */}
      <Modal open={!!previewImage} onClose={() => setPreviewImage(null)}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: 'rgba(0,0,0,0.9)',
          backdropFilter: 'blur(8px)'
        }}>
          <IconButton
            onClick={() => setPreviewImage(null)}
            sx={{
              position: 'absolute',
              top: 24,
              right: 24,
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.2)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>

          <IconButton
            onClick={() => handleDownload(previewImage)}
            sx={{
              position: 'absolute',
              top: 24,
              left: 24,
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.2)'
              }
            }}
          >
            <DownloadIcon />
          </IconButton>

          <Box sx={{
            maxWidth: '90vw',
            maxHeight: '90vh',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 2,
            boxShadow: 6
          }}>
            <img
              src={previewImage}
              alt="Prescription preview"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                borderRadius: 8
              }}
            />
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ImagePreviewModal;